import React, { useMemo } from 'react';
import cn from 'classnames';
import { Icon, P } from '@fashionnovateam/nova-ui-toolkit';
import SuppliersImage from '../../Assets/images/suppliers.jpg';
import InfluencersImage from '../../Assets/images/creators.jpg';

export type LoginLayoutProps = {
  children: React.ReactNode;
  isInfluencer: boolean;
  errorTitle?: string;
  errorDescription?: string;
};
function ErrorMessage({
  title,
  description,
  isExpired,
}: {
  title: string;
  description?: string;
  isExpired: boolean;
}) {
  return (
    <div
      className={cn('flex flex-row p-4 rounded-lg gap-3 items-center', {
        'bg-danger-100': !isExpired,
        'bg-[#FFF8EB]': isExpired,
      })}
    >
      {isExpired ? (
        <Icon.InformationCircle
          className="w-5 h-5 min-h-5 min-w-5"
          variant="solid"
          color="#C97118"
        />
      ) : (
        <Icon.MultipleCrossCancelCircle
          className="text-danger-700 w-5 h-5 min-h-5 min-w-5"
          variant="solid"
        />
      )}
      <div className="flex flex-col">
        <P size="sm">{title}</P>
        {description && (
          <P size="xs" color="disabled">
            {description}
          </P>
        )}
      </div>
    </div>
  );
}

// LoginLayout component defined as a function declaration
export function LoginLayout({
  children,
  isInfluencer,
  errorTitle,
  errorDescription,
}: LoginLayoutProps) {
  const backgroundImage = isInfluencer ? InfluencersImage : SuppliersImage;
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="twp h-full w-full">
      <img
        alt="Background"
        src={backgroundImage}
        className="md:w-[calc(100%-520px)] grow md:h-screen w-screen object-cover object-center absolute origin-top-left right-0"
      />
      <div
        className={cn(
          'absolute left-0 bottom-0 w-full h-fit',
          'md:relative md:left-0 md:top-0 md:w-[520px] md:h-full',
          'flex flex-col bg-base-primary-inverse max-h-screen overflow-auto',
        )}
      >
        <div className="py-10 px-5 md:px-[72px] flex-grow flex flex-col md:justify-center gap-10">
          <div className="flex flex-row items-center gap-[11px]">
            <Icon.FNLogo
              className={cn(
                isInfluencer ? 'rounded-full' : 'rounded-[20px]',
                'text-base-primary-inverse h-16 md:h-20 w-16 md:w-20 min-h-16 md:min-h-20 min-w-16 md:min-w-20 bg-black p-1',
              )}
            />
            <div className="font-black text-2xl flex gap-1">
              {isInfluencer ? 'CREATORS' : 'SUPPLIERS'}
              {isInfluencer && (
                <Icon.VerificationCheck
                  className="text-[#483BEB]"
                  variant="solid"
                />
              )}
            </div>
          </div>
          {(errorTitle || errorDescription) && (
            <ErrorMessage
              title={errorTitle || ''}
              description={errorDescription}
              isExpired={errorTitle === 'Access Code Expired'}
            />
          )}
          {children}
        </div>
        <div className="w-full flex flex-row items-center justify-center gap-2 pt-4 pb-6 text-2xs">
          <Icon.FNLogo />
          <span className="text-base-disabled">© {currentYear}</span>
        </div>
      </div>
    </div>
  );
}
